import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { StepProgress } from '../../components/ui/step-progress';
import type { StepState } from '../../components/ui/stepper';
import { useToast } from '../../components/ui/toast';
import type { OnboardingStep } from '@lizrd/contracts';
import { listDomains } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { VerifyStep } from './steps/VerifyStep';
import { DomainStep } from './steps/DomainStep';
import { ConnectStep } from './steps/ConnectStep';
import { SyncStep } from './steps/SyncStep';
import { CelebrateStep } from './steps/CelebrateStep';

/** The visible onboarding stages. `verify` is derived from emailVerified (not the
 * persisted onboardingStep); the terminal `done` step has no screen. */
// 'billing' and 'invite' are intentionally absent: payment is never collected during
// onboarding (reverse trial — the 7-day trial runs from signup and the day-8 paywall
// handles it), and inviting the team is a post-activation dashboard nudge (F7). Both
// are skipped server-side; any in-flight org that lands on them is auto-advanced below.
const DISPLAY_STEPS = [
  { key: 'verify', label: 'Verify' },
  { key: 'domain', label: 'Domain' },
  { key: 'connect', label: 'Connect' },
  { key: 'sync', label: 'Sync' },
  { key: 'celebrate', label: 'Start' },
];

/**
 * Mandatory guided first-run. Email verification is step 1 (staging/prod only —
 * local signups are pre-verified). After that it renders the screen for the org's
 * persisted onboarding step, resuming exactly where the user left off, and
 * advances the server-side pointer as each step completes. When the org flips to
 * ACTIVE, it hands off into the app — landing on the new domain's Cost
 * Optimization page.
 */
export function OnboardingPage() {
  const user = useAuthStore((s) => s.user);
  const advance = useAuthStore((s) => s.advanceOnboarding);
  const goBack = useAuthStore((s) => s.goBackOnboarding);
  const reloadUser = useAuthStore((s) => s.reloadUser);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Advancing the onboarding pointer is a server call that can fail (network, a
  // prerequisite the server re-checks, a stale pointer). Surface it instead of
  // swallowing it — a silent failure leaves the user staring at a dead button.
  const runAdvance = (from: OnboardingStep) => {
    void advance(from).catch((e: unknown) =>
      toast({
        title: "Couldn't continue",
        description: e instanceof Error ? e.message : 'Something went wrong — please try again.',
        tone: 'danger',
      }),
    );
  };

  const runBack = (from: OnboardingStep) => {
    void goBack(from).catch((e: unknown) =>
      toast({
        title: "Couldn't go back",
        description: e instanceof Error ? e.message : 'Something went wrong — please try again.',
        tone: 'danger',
      }),
    );
  };

  // The single domain created in the domain step (needed by later
  // steps + the final hand-off; queried so it survives a reload mid-onboarding).
  const envs = useQuery({ queryKey: ['domains'], queryFn: listDomains });
  const envId = envs.data?.[0]?.id;

  const active = user?.organizationStatus === 'ACTIVE';
  useEffect(() => {
    if (active) {
      navigate(envId ? `/domains/${envId}/overview` : '/', { replace: true });
    }
  }, [active, envId, navigate]);

  // 'billing' and 'invite' are no longer wizard steps. New orgs never stop on them
  // (the server skips both), but an org that was mid-flow on one when this shipped
  // would have no screen — auto-advance it past so it's never stranded.
  const emailVerified = user?.emailVerified;
  const onboardingStep = user?.onboardingStep;
  useEffect(() => {
    if (emailVerified && (onboardingStep === 'billing' || onboardingStep === 'invite')) {
      void advance(onboardingStep);
    }
  }, [emailVerified, onboardingStep, advance]);

  if (!user || active) return null;

  const step = user.onboardingStep;
  const stepKey = user.emailVerified ? step : 'verify';
  const activeIndex = DISPLAY_STEPS.findIndex((s) => s.key === stepKey);
  const progress = DISPLAY_STEPS.map((s, i) => ({
    label: s.label,
    state: (i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'pending') as StepState,
  }));

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <StepProgress steps={progress} spinActive={false} />
        {/* Standing reassurance on every step (ONB-010): overall expectation +
            that progress is saved and resumable. Pairs with the "Finish later"
            exit in the layout. */}
        <p className="text-center text-xs text-muted">
          Takes about five minutes · you can leave anytime and pick up right where you left off.
        </p>
      </div>
      {!user.emailVerified ? (
        <VerifyStep email={user.email} onVerified={() => void reloadUser()} />
      ) : (
        <>
          {step === 'domain' && <DomainStep onDone={() => runAdvance('domain')} />}
          {step === 'connect' && envId && (
            <ConnectStep
              envId={envId}
              onDone={() => runAdvance('connect')}
              onBack={() => runBack('connect')}
            />
          )}
          {step === 'sync' && envId && <SyncStep envId={envId} onDone={() => runAdvance('sync')} />}
          {step === 'celebrate' && <CelebrateStep onDone={() => runAdvance('celebrate')} />}
        </>
      )}
    </div>
  );
}
